import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ShieldCheck, AlertTriangle, Copy, CheckCircle2, Atom } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { computeSafetyNumber } from '../utils/safetyNumber';
import { RotationCadenceSparkline } from './RotationCadenceSparkline';
import { CadenceBadge } from './CadenceBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Deterministically derive a 30-hex-digit fingerprint from two
// Dilithium public keys — same UX shape as the classical safety
// number so the two sit side-by-side without mental overhead.
// Sort-then-concat-then-hash so Alice and Bob derive the same
// string regardless of which side is "me".
async function computePqFingerprint(myDilithiumPubB64, peerDilithiumPubB64) {
  const pair = [myDilithiumPubB64, peerDilithiumPubB64].sort();
  const joined = new TextEncoder().encode(pair.join('|'));
  const hashBuf = await window.crypto.subtle.digest('SHA-256', joined);
  const hex = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0')).join('');
  const first30 = hex.slice(0, 30);
  // Group as 5 × 6 so it reads cleanly over voice: "ab12cd 34ef56 …"
  return first30.match(/.{1,6}/g).join(' ');
}

/**
 * SafetyNumberDialog
 *
 * Shows the symmetric SHA-256 fingerprint of Alice's + Bob's public RSA
 * keys as a 30-digit number grouped for readability. The two users can
 * compare out-of-band (voice call, in-person). If the numbers differ, a
 * MITM has swapped one of the public keys — do not trust the channel.
 *
 * The dialog mounts on demand and re-derives both sides' fingerprints
 * client-side (no server round-trip beyond fetching the peer's public
 * key, which is a public field).
 */
export const SafetyNumberDialog = ({ isOpen, onClose, me, peer }) => {
  // `me` has {id, username, public_key (JWK string or object)}
  // `peer` is a minimal contact: {id, username}
  const [safetyNumber, setSafetyNumber] = useState(null);
  const [pqFingerprint, setPqFingerprint] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !me || !peer?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSafetyNumber(null);
      setPqFingerprint(null);
      try {
        const peerRes = await axios.get(`${API}/users/${peer.id}`);
        const peerJwk =
          typeof peerRes.data?.public_key === 'string'
            ? JSON.parse(peerRes.data.public_key)
            : peerRes.data?.public_key;
        const myKeysRaw = window.localStorage.getItem('ghostcomm_keys');
        const myJwk = myKeysRaw ? JSON.parse(myKeysRaw)?.publicKey : null;
        if (!peerJwk || !myJwk) {
          throw new Error('Missing public keys on one side');
        }
        const number = await computeSafetyNumber(myJwk, peerJwk);
        if (!cancelled) setSafetyNumber(number);

        // Also fetch the peer's Dilithium pubkey for the PQ fingerprint.
        // 404 is expected for legacy peers with no PQ bundle — we just
        // omit the PQ panel in that case; the classical number still
        // stands on its own.
        try {
          const bundleRes = await axios.get(`${API}/users/${peer.id}/pq-bundle`);
          const peerDilB64 = bundleRes.data?.dilithium_pub;
          let myDilB64 = null;
          try {
            const pqRaw = localStorage.getItem('ghostcomm_pq_keys');
            if (pqRaw) myDilB64 = JSON.parse(pqRaw)?.dilithium_pub;
          } catch { /* noop */ }
          if (peerDilB64 && myDilB64) {
            const fp = await computePqFingerprint(myDilB64, peerDilB64);
            if (!cancelled) setPqFingerprint(fp);
          }
        } catch { /* peer has no PQ bundle — fine, classical only */ }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'Could not derive number');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, me, peer?.id]);

  const copyNumber = async () => {
    if (!safetyNumber || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(safetyNumber);
      toast.success('Safety number copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Verify {peer?.username}'s identity
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Compare this number with {peer?.username} over a separate trusted channel
            (voice call, in person). If the numbers match on both sides, no one has
            tampered with your keys.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <p className="text-sm text-slate-500 italic py-4" data-testid="safety-number-loading">
            Computing…
          </p>
        )}

        {error && (
          <div
            className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
            data-testid="safety-number-error"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {safetyNumber && (
          <div className="space-y-3" data-testid="safety-number-display">
            <div className="font-mono text-center text-lg tracking-wider bg-background/50 rounded-lg p-4 border border-border select-all break-words">
              {safetyNumber}
            </div>
            <div className="flex items-center gap-2 justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={copyNumber}
                className="text-xs"
                data-testid="safety-number-copy"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>

            {pqFingerprint && (
              <div
                className="space-y-2 pt-2 border-t border-border/60"
                data-testid="pq-fingerprint-display"
              >
                <div className="flex items-center gap-2 text-xs text-violet-300/90">
                  <Atom className="w-3.5 h-3.5" />
                  <span className="font-semibold">
                    Post-quantum fingerprint (ML-DSA-65)
                  </span>
                </div>
                <div className="font-mono text-center text-sm tracking-wider bg-violet-950/20 rounded-lg p-3 border border-violet-500/30 select-all break-words text-violet-100">
                  {pqFingerprint}
                </div>
                <div className="flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      try {
                        navigator.clipboard?.writeText(pqFingerprint);
                        toast.success('PQ fingerprint copied');
                      } catch {
                        toast.error('Copy failed');
                      }
                    }}
                    className="text-xs text-violet-300 hover:text-violet-100"
                    data-testid="pq-fingerprint-copy"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="text-[11px] text-violet-200/70 leading-relaxed">
                  Compare BOTH the classical number above and this
                  post-quantum fingerprint out-of-band. If a future
                  quantum computer forges RSA, the ML-DSA-65 fingerprint
                  is the remaining anchor — it must match on both sides.
                </p>
              </div>
            )}

            <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                If the numbers match on both sides, your conversation with{' '}
                {peer?.username} is end-to-end encrypted with keys that haven't been
                tampered with. If they don't match, assume a MITM attack — do not
                send anything sensitive.
              </span>
            </div>

            {/* Soft trust signal — peers who rotate regularly are visibly
                healthy participants; a long flat line is the opposite. */}
            {peer?.id && (
              <RotationCadenceSparkline
                userId={peer.id}
                className="text-sky-400 pt-1"
                label={`${peer.username}'s rotation rhythm`}
              />
            )}
            {peer?.id && (
              <div className="pt-1">
                <CadenceBadge userId={peer.id} variant="verbose" />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SafetyNumberDialog;
